import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

(function () {
  const FLAG = '__cartWarrantySyncInit';
  if (window[FLAG]) return;
  window[FLAG] = true;

  let syncTimeout = null;
  let previousCartState = null;
  const CHECKOUT_BUTTON_FALLBACK_MS = 5000;
  const CHECKOUT_REAPPLY_DISABLE_MS = 250;
  let checkoutReenableFallbackTimeout = null;
  let checkoutDisabledForCcoSync = false;

  function getCheckoutButton() {
    return (
      document.getElementById('checkout') ||
      document.querySelector('button[name="checkout"][form="cart-form"]') ||
      document.querySelector('.cart__checkout-button')
    );
  }

  function disableCheckoutButton() {
    checkoutDisabledForCcoSync = true;
    const btn = getCheckoutButton();
    if (btn) {
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
    }
  }

  function reenableCheckoutButton() {
    checkoutDisabledForCcoSync = false;
    if (checkoutReenableFallbackTimeout) {
      clearTimeout(checkoutReenableFallbackTimeout);
      checkoutReenableFallbackTimeout = null;
    }
    const btn = getCheckoutButton();
    if (btn) {
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
    }
  }

  function scheduleCheckoutReenableFallback() {
    if (checkoutReenableFallbackTimeout) {
      clearTimeout(checkoutReenableFallbackTimeout);
    }
    checkoutReenableFallbackTimeout = setTimeout(() => {
      checkoutReenableFallbackTimeout = null;
      checkoutDisabledForCcoSync = false;
      reenableCheckoutButton();
    }, CHECKOUT_BUTTON_FALLBACK_MS);
  }

  function getWarrantyVariantIdFromSku() {
    const config = window.sparkAddOnSkuConfig;
    const warrantySku = config?.warranty || 'Lifetime Torsion Warranty';
    const scriptTag = document.querySelector(`script[type="application/json"][data-warranty-sku-map]`);
    if (!scriptTag || !scriptTag.textContent) {
      return null;
    }

    try {
      const skuMap = JSON.parse(scriptTag.textContent.trim());
      const variantId = skuMap[warrantySku] || null;
      return variantId ? String(variantId) : null;
    } catch (e) {
      return null;
    }
  }

  function getCustomColorVariantId() {
    const config = window.sparkAddOnSkuConfig;
    const customColorSku = config?.customColor || 'Custom Color Option';
    const scriptTag = document.querySelector(`script[type="application/json"][data-custom-color-sku-map]`);
    if (!scriptTag || !scriptTag.textContent) return null;

    try {
      const skuMap = JSON.parse(scriptTag.textContent.trim());
      const variantId = skuMap[customColorSku] || null;
      return variantId ? String(variantId) : null;
    } catch (e) {
      return null;
    }
  }

  async function getCurrentCart() {
    try {
      if (!Theme?.routes?.cart_url) {
        return null;
      }
      const response = await fetch(Theme.routes.cart_url + '.js');
      if (!response.ok) {
        return null;
      }
      const cart = await response.json();
      return cart || null;
    } catch (error) {
      return null;
    }
  }

  function countUniqueBaseVariants(cartItems) {
    if (!cartItems || !Array.isArray(cartItems)) {
      return 0;
    }

    const warrantyVariantId = getWarrantyVariantIdFromSku();
    const customColorVariantIds = getAllCustomColorVariantIds(cartItems);
    const uniqueVariants = new Set();

    cartItems.forEach((item) => {
      const variantId = String(item.variant_id);
      if (warrantyVariantId && variantId === warrantyVariantId) {
        return;
      }
      if (isCustomColorLineItem(item, customColorVariantIds)) {
        return;
      }
      uniqueVariants.add(variantId);
    });

    return uniqueVariants.size;
  }

  function getWarrantyLineItem(cartItems) {
    if (!cartItems || !Array.isArray(cartItems)) {
      return null;
    }

    const warrantyVariantId = getWarrantyVariantIdFromSku();
    if (!warrantyVariantId) {
      return null;
    }

    let lineIndex = null;
    const warrantyItem = cartItems.find((item, index) => {
      if (String(item.variant_id) === warrantyVariantId) {
        lineIndex = index + 1;
        return true;
      }
      return false;
    });

    if (!warrantyItem || !lineIndex) {
      return null;
    }

    const result = {
      line: lineIndex,
      key: String(warrantyItem.key || warrantyItem.id),
      quantity: warrantyItem.quantity || 0,
    };
    return result;
  }

  /**
   * Returns all warranty line items (cart can have multiple warranty lines with different Associated Product).
   * @param {Array<any>} cartItems
   * @returns {{ line: number; quantity: number; associatedProduct: string }[]}
   */
  function getWarrantyLineItems(cartItems) {
    if (!cartItems || !Array.isArray(cartItems)) return [];
    const warrantyVariantId = getWarrantyVariantIdFromSku();
    if (!warrantyVariantId) return [];
    const result = [];
    cartItems.forEach((item, index) => {
      if (String(item.variant_id) !== warrantyVariantId) return;
      const associatedProduct = (item.properties?.['Associated Product'] || '').trim();
      result.push({
        line: index + 1,
        quantity: item.quantity || 0,
        associatedProduct,
      });
    });
    return result;
  }

  function getMainProductSku(item) {
    if (!item) return '';
    return (item.sku || item.properties?.SKU || item.variant_sku || '').trim();
  }

  function hasWarrantyProperty(item) {
    if (!item || !item.properties) return false;
    const warrantyProp = item.properties['This product has opted for warranty'];
    return warrantyProp === 'Yes';
  }

  function hasCustomColorProperty(item) {
    if (!item || !item.properties) return false;
    const paintColorCode = item.properties['Paint Color Code'];
    return paintColorCode && paintColorCode.trim() !== '';
  }

  function getCustomColorSkuFromItem(item) {
    if (!item || !item.properties) return null;
    const feeProperty = item.properties['Non-Refundable Custom Color Fee'];
    if (!feeProperty) return null;

    const priceMatch = feeProperty.match(/\$?([\d.]+)/);
    if (!priceMatch) return null;

    const price = parseFloat(priceMatch[1]);
    if (isNaN(price)) return null;

    const priceString = price.toFixed(2).replace(/\.00$/, '').replace(/\.0$/, '');
    return `CCO-${priceString}`;
  }

  function getVariantIdFromSku(sku) {
    if (!sku) return null;
    const scriptTag = document.querySelector(`script[type="application/json"][data-custom-color-sku-map]`);
    if (!scriptTag || !scriptTag.textContent) return null;

    try {
      const skuMap = JSON.parse(scriptTag.textContent.trim());
      const variantId = skuMap[sku] || null;
      return variantId ? String(variantId) : null;
    } catch (e) {
      return null;
    }
  }

  function getMappedCustomColorVariantIds() {
    const scriptTag = document.querySelector(`script[type="application/json"][data-custom-color-sku-map]`);
    if (!scriptTag || !scriptTag.textContent) {
      return new Set();
    }

    try {
      const skuMap = JSON.parse(scriptTag.textContent.trim());
      const variantIds = new Set();
      for (const variantId of Object.values(skuMap)) {
        if (variantId) {
          variantIds.add(String(variantId));
        }
      }
      return variantIds;
    } catch (e) {
      return new Set();
    }
  }

  function isCustomColorLineItem(item, customColorVariantIds = null) {
    if (!item) return false;

    const variantId = String(item.variant_id || '');
    if (customColorVariantIds && customColorVariantIds.has(variantId)) {
      return true;
    }

    const handle = String(item.handle || item.product_handle || '').trim();
    if (handle === 'custom-color-option') {
      return true;
    }

    const associatedProduct = (item.properties?.['Associated Product'] || '').trim();
    const skuProperty = String(item.properties?.SKU || '')
      .trim()
      .toUpperCase();
    const lineItemSku = String(item.sku || '')
      .trim()
      .toUpperCase();

    if (associatedProduct && (skuProperty.startsWith('CCO-') || lineItemSku.startsWith('CCO-'))) {
      return true;
    }

    return false;
  }

  function getAllCustomColorVariantIds(cartItems = null) {
    const variantIds = getMappedCustomColorVariantIds();

    if (!cartItems || !Array.isArray(cartItems)) {
      return variantIds;
    }

    cartItems.forEach((item) => {
      if (!isCustomColorLineItem(item, variantIds)) return;
      if (item.variant_id) {
        variantIds.add(String(item.variant_id));
      }
    });

    return variantIds;
  }

  function getCustomColorLineItemBySku(cartItems, sku, colorCode) {
    if (!cartItems || !Array.isArray(cartItems) || !sku) {
      return null;
    }

    const variantId = getVariantIdFromSku(sku);
    const normalizedSku = sku.trim().toUpperCase();
    const customColorVariantIds = getAllCustomColorVariantIds(cartItems);

    let lineIndex = null;
    const customColorItem = cartItems.find((item, index) => {
      const itemVariantId = String(item.variant_id);
      const itemSkuProperty = String(item.properties?.SKU || '')
        .trim()
        .toUpperCase();

      const matchesVariant = variantId ? itemVariantId === variantId : false;
      const matchesSku = !variantId && itemSkuProperty === normalizedSku;

      if (!matchesVariant && !matchesSku) return false;
      if (!isCustomColorLineItem(item, customColorVariantIds)) return false;

      if (colorCode) {
        const itemColorCode = item.properties?.['Paint Color Code'];
        if (!itemColorCode || itemColorCode.trim() !== colorCode.trim()) return false;
      }
      lineIndex = index + 1;
      return true;
    });

    if (!customColorItem || !lineIndex) {
      return null;
    }

    const result = {
      line: lineIndex,
      key: String(customColorItem.key || customColorItem.id),
      quantity: customColorItem.quantity || 0,
    };
    return result;
  }

  function createCcoLineItemTracker(cartItems) {
    const customColorVariantIds = getAllCustomColorVariantIds(cartItems);
    const ccoItems = [];

    if (!cartItems || !Array.isArray(cartItems)) return ccoItems;

    cartItems.forEach((item) => {
      if (!isCustomColorLineItem(item, customColorVariantIds)) return;
      const variantId = String(item.variant_id);

      const paintColorCode = (item.properties?.['Paint Color Code'] || '').trim();
      const associatedProduct = (item.properties?.['Associated Product'] || '').trim();

      ccoItems.push({
        variantId,
        paintColorCode,
        associatedProduct,
        quantity: item.quantity || 0,
      });
    });

    return ccoItems;
  }

  async function removeColorPropertiesFromMainProduct(cartItems, paintColorCode, associatedSku) {
    if (!cartItems || !Array.isArray(cartItems) || !paintColorCode) return;

    const customColorVariantIds = getAllCustomColorVariantIds(cartItems);
    const warrantyVariantId = getWarrantyVariantIdFromSku();
    const mainSkuTrimmed = (associatedSku || '').trim();

    let mainProductLineIndex = -1;
    const mainProduct = cartItems.find((item, index) => {
      const variantId = String(item.variant_id);
      if (isCustomColorLineItem(item, customColorVariantIds)) return false;
      if (warrantyVariantId && variantId === warrantyVariantId) return false;

      const itemColorCode = (item.properties?.['Paint Color Code'] || '').trim();
      if (itemColorCode !== paintColorCode) return false;
      if (mainSkuTrimmed) {
        const itemSku = (item.sku || item.properties?.SKU || item.variant_sku || '').trim();
        if (itemSku !== mainSkuTrimmed) return false;
      }
      mainProductLineIndex = index;
      return true;
    });

    if (!mainProduct || mainProductLineIndex === -1) return;

    const newProperties = {};
    if (mainProduct.properties) {
      for (const [key, value] of Object.entries(mainProduct.properties)) {
        if (key === 'Paint Color Code' || key === 'Non-Refundable Custom Color Fee') continue;
        newProperties[key] = value;
      }
    }

    try {
      if (!Theme?.routes?.cart_change_url) return;

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set();
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const body = JSON.stringify({
        line: mainProductLineIndex + 1,
        quantity: mainProduct.quantity,
        properties: newProperties,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
      if (!response.ok) return;

      const responseText = await response.text();
      const parsedResponse = JSON.parse(responseText);

      if (parsedResponse.errors) return;

      document.dispatchEvent(
        new CartUpdateEvent({}, '', {
          itemCount: parsedResponse.item_count || 0,
          source: 'cart-warranty-sync',
          sections: parsedResponse.sections || {},
        }),
      );
    } catch (error) {}
  }

  async function stripOrphanedWarrantyAttributesOnCart(cartItems) {
    const warrantyVariantId = getWarrantyVariantIdFromSku();
    if (!warrantyVariantId || !cartItems?.length) return;

    const warrantyLines = getWarrantyLineItems(cartItems);
    const associatedSkusWithQty = new Set(
      warrantyLines
        .filter((w) => w.quantity > 0 && (w.associatedProduct || '').trim())
        .map((w) => (w.associatedProduct || '').trim()),
    );

    const customColorVariantIds = getAllCustomColorVariantIds(cartItems);
    const skusToStrip = new Set();
    cartItems.forEach((item) => {
      const variantId = String(item.variant_id);
      if (warrantyVariantId && variantId === warrantyVariantId) return;
      if (isCustomColorLineItem(item, customColorVariantIds)) return;
      if (!hasWarrantyProperty(item)) return;
      const sku = getMainProductSku(item);
      if (!sku) return;
      if (!associatedSkusWithQty.has(sku)) skusToStrip.add(sku);
    });

    for (const sku of skusToStrip) {
      await removeWarrantyPropertyFromMainProduct(cartItems, sku);
      const freshCart = await getCurrentCart();
      if (freshCart?.items) cartItems = freshCart.items;
    }
  }

  async function removeWarrantyPropertyFromMainProduct(cartItems, associatedSku) {
    if (!cartItems || !Array.isArray(cartItems) || !associatedSku) return;

    const warrantyVariantId = getWarrantyVariantIdFromSku();
    const customColorVariantIds = getAllCustomColorVariantIds(cartItems);
    const skuTrimmed = String(associatedSku).trim();
    if (!skuTrimmed) return;

    let mainProductLineIndex = -1;
    const mainProduct = cartItems.find((item, index) => {
      const variantId = String(item.variant_id);
      if (isCustomColorLineItem(item, customColorVariantIds)) return false;
      if (warrantyVariantId && variantId === warrantyVariantId) return false;
      if (!hasWarrantyProperty(item)) return false;
      if (getMainProductSku(item) !== skuTrimmed) return false;
      mainProductLineIndex = index;
      return true;
    });

    if (!mainProduct || mainProductLineIndex === -1) return;

    const newProperties = {};
    if (mainProduct.properties) {
      for (const [key, value] of Object.entries(mainProduct.properties)) {
        if (key === 'This product has opted for warranty') continue;
        newProperties[key] = value;
      }
    }

    try {
      if (!Theme?.routes?.cart_change_url) return;

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set();
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const body = JSON.stringify({
        line: mainProductLineIndex + 1,
        quantity: mainProduct.quantity,
        properties: newProperties,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
      if (!response.ok) return;

      const responseText = await response.text();
      const parsedResponse = JSON.parse(responseText);

      if (parsedResponse.errors) return;

      document.dispatchEvent(
        new CartUpdateEvent({}, '', {
          itemCount: parsedResponse.item_count || 0,
          source: 'cart-warranty-sync',
          sections: parsedResponse.sections || {},
        }),
      );
    } catch (error) {}
  }

  function getMainProductSkusInCart(cartItems) {
    if (!cartItems || !Array.isArray(cartItems)) return new Set();
    const warrantyVariantId = getWarrantyVariantIdFromSku();
    const customColorVariantIds = getAllCustomColorVariantIds(cartItems);
    const skus = new Set();
    cartItems.forEach((item) => {
      const variantId = String(item.variant_id);
      if (warrantyVariantId && variantId === warrantyVariantId) return;
      if (isCustomColorLineItem(item, customColorVariantIds)) return;
      const sku = (item.sku || item.properties?.SKU || item.variant_sku || '').trim();
      if (sku) skus.add(sku);
    });
    return skus;
  }

  async function updateCartLineProperties(lineNumber, quantity, properties) {
    try {
      if (!Theme?.routes?.cart_change_url) return;

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set();
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const body = JSON.stringify({
        line: lineNumber,
        quantity,
        properties,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
      if (!response.ok) return;

      const responseText = await response.text();
      const parsedResponse = JSON.parse(responseText);

      if (parsedResponse.errors) return;

      document.dispatchEvent(
        new CartUpdateEvent({}, '', {
          itemCount: parsedResponse.item_count || 0,
          source: 'cart-warranty-sync',
          sections: parsedResponse.sections || {},
        }),
      );
    } catch (error) {}
  }

  async function clearOrphanedWarrantyAssociatedProduct(cartItems) {
    if (!cartItems || !Array.isArray(cartItems)) return;

    const warrantyVariantId = getWarrantyVariantIdFromSku();
    if (!warrantyVariantId) return;

    const mainProductSkus = getMainProductSkusInCart(cartItems);

    const warrantyLine = getWarrantyLineItem(cartItems);
    if (!warrantyLine) return;

    const warrantyItem = cartItems.find((item) => String(item.variant_id) === warrantyVariantId);
    if (!warrantyItem?.properties) return;

    const associatedSku = (warrantyItem.properties['Associated Product'] || '').trim();
    if (!associatedSku) return;
    if (mainProductSkus.has(associatedSku)) return;

    const newProperties = {};
    for (const [key, value] of Object.entries(warrantyItem.properties)) {
      if (key === 'Associated Product') continue;
      newProperties[key] = value;
    }

    await updateCartLineProperties(warrantyLine.line, warrantyItem.quantity, newProperties);
  }

  async function updateCartLineQuantity(lineNumber, newQuantity) {
    try {
      if (!Theme?.routes?.cart_change_url) return;

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set();
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const body = JSON.stringify({
        line: lineNumber,
        quantity: newQuantity,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
      if (!response.ok) return;

      const responseText = await response.text();
      const parsedResponse = JSON.parse(responseText);

      if (parsedResponse.errors) return;

      document.dispatchEvent(
        new CartUpdateEvent({}, '', {
          itemCount: parsedResponse.item_count || 0,
          source: 'cart-warranty-sync',
          sections: parsedResponse.sections || {},
        }),
      );
    } catch (error) {}
  }

  async function ensureCcoQuantitiesMatchMainProducts() {
    const cart = await getCurrentCart();
    if (!cart || !cart.items) return;

    const cartItems = cart.items;
    const customColorVariantIds = getAllCustomColorVariantIds(cartItems);
    const warrantyVariantId = getWarrantyVariantIdFromSku();

    if (customColorVariantIds.size === 0) return;

    const mainProductQtyMap = new Map();

    cartItems.forEach((item) => {
      const variantId = String(item.variant_id);
      if (isCustomColorLineItem(item, customColorVariantIds)) return;
      if (warrantyVariantId && variantId === warrantyVariantId) return;

      const paintColorCode = (item.properties?.['Paint Color Code'] || '').trim();
      if (!paintColorCode) return;

      const mainSku = (item.sku || item.properties?.SKU || item.variant_sku || '').trim();
      if (!mainSku) return;
      const key = paintColorCode + '||' + mainSku;
      const currentQty = mainProductQtyMap.get(key) || 0;
      mainProductQtyMap.set(key, currentQty + (item.quantity || 0));
    });

    for (let i = cartItems.length - 1; i >= 0; i--) {
      const item = cartItems[i];
      if (!isCustomColorLineItem(item, customColorVariantIds)) continue;

      const ccoPaintColorCode = (item.properties?.['Paint Color Code'] || '').trim();
      const ccoAssociatedSku = (item.properties?.['Associated Product'] || '').trim();
      if (!ccoPaintColorCode) continue;

      const key = ccoPaintColorCode + '||' + ccoAssociatedSku;
      const expectedQty = mainProductQtyMap.get(key);

      if (expectedQty !== undefined && expectedQty > 0) {
        if (item.quantity !== expectedQty) {
          await updateCartLineQuantity(i + 1, expectedQty);
        }
      } else if (ccoAssociatedSku) {
        await updateCartLineQuantity(i + 1, 0);
      }
    }
  }

  function createVariantQuantityMap(cartItems) {
    const map = new Map();
    const warrantyVariantId = getWarrantyVariantIdFromSku();
    const customColorVariantIds = getAllCustomColorVariantIds(cartItems);

    if (!cartItems || !Array.isArray(cartItems)) return map;

    cartItems.forEach((item) => {
      const variantId = String(item.variant_id);
      if (warrantyVariantId && variantId === warrantyVariantId) return;
      if (isCustomColorLineItem(item, customColorVariantIds)) return;

      const quantity = item.quantity || 0;
      map.set(variantId, quantity + (map.get(variantId) || 0));
    });

    return map;
  }

  function createWarrantyVariantQuantityMap(cartItems) {
    const map = new Map();
    const warrantyVariantId = getWarrantyVariantIdFromSku();
    const customColorVariantIds = getAllCustomColorVariantIds(cartItems);

    if (!cartItems || !Array.isArray(cartItems)) return map;

    cartItems.forEach((item) => {
      if (!hasWarrantyProperty(item)) return;

      const variantId = String(item.variant_id);
      if (warrantyVariantId && variantId === warrantyVariantId) return;
      if (isCustomColorLineItem(item, customColorVariantIds)) return;

      const quantity = item.quantity || 0;
      map.set(variantId, quantity + (map.get(variantId) || 0));
    });

    return map;
  }

  function createCustomColorVariantQuantityMap(cartItems) {
    const variantMap = new Map();
    const skuMap = new Map();
    const colorCodeMap = new Map();
    const warrantyVariantId = getWarrantyVariantIdFromSku();
    const customColorVariantIds = getAllCustomColorVariantIds(cartItems);

    if (!cartItems || !Array.isArray(cartItems)) {
      return { variantMap, skuMap, colorCodeMap };
    }

    cartItems.forEach((item) => {
      if (!hasCustomColorProperty(item)) return;

      const variantId = String(item.variant_id);
      if (warrantyVariantId && variantId === warrantyVariantId) return;
      if (isCustomColorLineItem(item, customColorVariantIds)) return;

      const quantity = item.quantity || 0;
      variantMap.set(variantId, quantity + (variantMap.get(variantId) || 0));

      const sku = getCustomColorSkuFromItem(item);
      if (sku) {
        skuMap.set(variantId, sku);
      }

      const paintColorCode = item.properties?.['Paint Color Code'];
      if (paintColorCode) {
        colorCodeMap.set(variantId, paintColorCode.trim());
      }
    });

    return { variantMap, skuMap, colorCodeMap };
  }

  function calculateWarrantyReduction(beforeMap, afterMap, beforeWarrantyMap) {
    let reduction = 0;

    beforeWarrantyMap.forEach((beforeQty, variantId) => {
      const afterQty = afterMap.get(variantId) || 0;

      if (beforeQty > 0 && afterQty === 0) {
        reduction += beforeQty;
      }
    });

    return reduction;
  }

  /**
   * Builds variantId -> SKU for main products (for matching removed products to warranty lines by Associated Product).
   * @param {Array<any>} cartItems
   * @returns {Map<string, string>}
   */
  function createVariantIdToSkuMap(cartItems) {
    const map = new Map();
    const warrantyVariantId = getWarrantyVariantIdFromSku();
    const customColorVariantIds = getAllCustomColorVariantIds(cartItems);
    if (!cartItems || !Array.isArray(cartItems)) return map;
    cartItems.forEach((item) => {
      const variantId = String(item.variant_id);
      if (warrantyVariantId && variantId === warrantyVariantId) return;
      if (isCustomColorLineItem(item, customColorVariantIds)) return;
      const sku = getMainProductSku(item);
      if (sku) map.set(variantId, sku);
    });
    return map;
  }

  /**
   * Returns warranty reduction per associated SKU (so we only reduce the warranty line that matches the removed product).
   * @param {Map<string, number>} beforeMap
   * @param {Map<string, number>} afterMap
   * @param {Map<string, number>} beforeWarrantyMap
   * @param {Map<string, string>} variantIdToSku
   * @returns {{ associatedSku: string; reduction: number }[]}
   */
  function calculateWarrantyReductionPerSku(beforeMap, afterMap, beforeWarrantyMap, variantIdToSku) {
    const reductions = [];
    beforeWarrantyMap.forEach((beforeQty, variantId) => {
      const afterQty = afterMap.get(variantId) || 0;
      if (beforeQty <= 0 || afterQty >= beforeQty) return;
      const reduction = beforeQty - afterQty;
      const associatedSku = variantIdToSku.get(variantId);
      if (associatedSku) {
        reductions.push({ associatedSku, reduction });
      }
    });
    return reductions;
  }

  function calculateCustomColorReductions(
    beforeMap,
    afterMap,
    beforeCustomColorVariantMap,
    beforeCustomColorSkuMap,
    beforeCustomColorCodeMap,
  ) {
    const reductions = [];

    beforeCustomColorVariantMap.forEach((beforeQty, variantId) => {
      const afterQty = afterMap.get(variantId) || 0;

      if (beforeQty > 0 && afterQty === 0) {
        const sku = beforeCustomColorSkuMap.get(variantId);
        const colorCode = beforeCustomColorCodeMap ? beforeCustomColorCodeMap.get(variantId) : null;
        if (sku) {
          reductions.push({ sku, colorCode, reduction: beforeQty });
        }
      }
    });

    return reductions;
  }

  async function updateWarrantyQuantity(reduction, currentWarrantyQty) {
    if (reduction <= 0) return;

    const warrantyLineItem = await (async () => {
      const cart = await getCurrentCart();
      return cart ? getWarrantyLineItem(cart.items) : null;
    })();

    if (!warrantyLineItem) return;

    const newQuantity = Math.max(0, currentWarrantyQty - reduction);

    if (newQuantity === currentWarrantyQty) return;

    try {
      if (!Theme?.routes?.cart_change_url) return;

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set();
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const body = JSON.stringify({
        line: warrantyLineItem.line,
        quantity: newQuantity,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
      if (!response.ok) return;

      const responseText = await response.text();
      const parsedResponse = JSON.parse(responseText);

      if (parsedResponse.errors) return;

      document.dispatchEvent(
        new CartUpdateEvent({}, '', {
          itemCount: parsedResponse.item_count || 0,
          source: 'cart-warranty-sync',
          sections: parsedResponse.sections || {},
        }),
      );
    } catch (error) {}
  }

  async function updateCustomColorQuantityBySku(sku, reduction, colorCode) {
    if (reduction <= 0 || !sku) return;

    const cart = await getCurrentCart();
    if (!cart || !cart.items) return;

    const customColorLineItem = getCustomColorLineItemBySku(cart.items, sku, colorCode);
    if (!customColorLineItem) return;

    const actualCurrentQty = customColorLineItem.quantity || 0;

    const newQuantity = Math.max(0, actualCurrentQty - reduction);

    if (newQuantity === actualCurrentQty) return;

    try {
      if (!Theme?.routes?.cart_change_url) return;

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set();
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const body = JSON.stringify({
        line: customColorLineItem.line,
        quantity: newQuantity,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
      if (!response.ok) return;

      const responseText = await response.text();
      const parsedResponse = JSON.parse(responseText);

      if (parsedResponse.errors) return;

      document.dispatchEvent(
        new CartUpdateEvent({}, '', {
          itemCount: parsedResponse.item_count || 0,
          source: 'cart-warranty-sync',
          sections: parsedResponse.sections || {},
        }),
      );
    } catch (error) {}
  }

  async function syncWarrantyQuantity() {
    try {
      const currentCart = await getCurrentCart();
      if (!currentCart || !currentCart.items) return;

      const currentItems = currentCart.items;
      const warrantyVariantId = getWarrantyVariantIdFromSku();
      const customColorVariantIds = getAllCustomColorVariantIds(currentItems);

      const warrantyItem = warrantyVariantId
        ? currentItems.find((item) => String(item.variant_id) === warrantyVariantId)
        : null;
      const currentWarrantyQty = warrantyItem?.quantity || 0;

      const customColorItem =
        customColorVariantIds.size > 0
          ? currentItems.find((item) => isCustomColorLineItem(item, customColorVariantIds))
          : null;
      const currentCustomColorQty = customColorItem?.quantity || 0;

      const currentCcoLineItems = createCcoLineItemTracker(currentItems);

      const currentWarrantyLines = getWarrantyLineItems(currentItems);
      const totalWarrantyQty = currentWarrantyLines.reduce((sum, w) => sum + w.quantity, 0);
      const variantIdToSku = createVariantIdToSkuMap(currentItems);

      if (!previousCartState) {
        const customColorData = createCustomColorVariantQuantityMap(currentItems);
        previousCartState = {
          variantMap: createVariantQuantityMap(currentItems),
          warrantyVariantMap: createWarrantyVariantQuantityMap(currentItems),
          customColorVariantMap: customColorData.variantMap,
          customColorSkuMap: customColorData.skuMap,
          customColorCodeMap: customColorData.colorCodeMap,
          ccoLineItems: currentCcoLineItems,
          previousWarrantyQty: totalWarrantyQty,
          previousWarrantyLines: currentWarrantyLines,
          variantIdToSku,
        };
        return;
      }

      const currentVariantMap = createVariantQuantityMap(currentItems);
      const currentWarrantyVariantMap = createWarrantyVariantQuantityMap(currentItems);
      const currentCustomColorData = createCustomColorVariantQuantityMap(currentItems);
      const currentCustomColorVariantMap = currentCustomColorData.variantMap;
      const currentCustomColorSkuMap = currentCustomColorData.skuMap;
      const currentCustomColorCodeMap = currentCustomColorData.colorCodeMap;

      if (warrantyVariantId) {
        const previousWarrantyLines = previousCartState.previousWarrantyLines || [];
        for (const prev of previousWarrantyLines) {
          if (!prev.associatedProduct) continue;
          const curr = currentWarrantyLines.find((w) => w.associatedProduct === prev.associatedProduct);
          const currQty = curr ? curr.quantity : 0;
          if (currQty === 0 && prev.quantity > 0) {
            const freshCart = await getCurrentCart();
            if (freshCart?.items) {
              await removeWarrantyPropertyFromMainProduct(freshCart.items, prev.associatedProduct);
            }
          }
        }

        const prevVariantIdToSku = previousCartState.variantIdToSku || new Map();
        const currentWarrantedQtyBySku = new Map();
        currentWarrantyVariantMap.forEach((qty, variantId) => {
          const sku = variantIdToSku.get(variantId);
          if (sku) currentWarrantedQtyBySku.set(sku, (currentWarrantedQtyBySku.get(sku) || 0) + qty);
        });
        const freshCart = await getCurrentCart();
        if (freshCart?.items) {
          const warrantyLines = getWarrantyLineItems(freshCart.items);
          for (const warrantyLine of warrantyLines) {
            const associatedSku = (warrantyLine.associatedProduct || '').trim();
            if (!associatedSku) continue;
            const expectedQty = currentWarrantedQtyBySku.get(associatedSku) ?? 0;
            if (warrantyLine.quantity !== expectedQty) {
              await updateCartLineQuantity(warrantyLine.line, expectedQty);
            }
          }
        }
      }

      if (previousCartState.ccoLineItems && previousCartState.ccoLineItems.length > 0) {
        let checkoutWasDisabled = false;
        for (const prevCco of previousCartState.ccoLineItems) {
          if (!prevCco.paintColorCode) continue;

          const stillExists = currentCcoLineItems.some(
            (curr) =>
              curr.paintColorCode === prevCco.paintColorCode && curr.associatedProduct === prevCco.associatedProduct,
          );

          if (!stillExists) {
            if (!checkoutWasDisabled) {
              disableCheckoutButton();
              scheduleCheckoutReenableFallback();
              checkoutWasDisabled = true;
            }

            const freshCart = await getCurrentCart();
            if (freshCart && freshCart.items) {
              await removeColorPropertiesFromMainProduct(
                freshCart.items,
                prevCco.paintColorCode,
                prevCco.associatedProduct,
              );
            }
          }
        }
        if (checkoutWasDisabled) {
          reenableCheckoutButton();
        }
      }

      if (customColorVariantIds.size > 0) {
        await ensureCcoQuantitiesMatchMainProducts();
      }

      if (warrantyVariantId) {
        await clearOrphanedWarrantyAssociatedProduct(currentItems);
      }

      previousCartState = {
        variantMap: currentVariantMap,
        warrantyVariantMap: currentWarrantyVariantMap,
        customColorVariantMap: currentCustomColorVariantMap,
        customColorSkuMap: currentCustomColorSkuMap,
        customColorCodeMap: currentCustomColorCodeMap,
        ccoLineItems: currentCcoLineItems,
        previousWarrantyQty: totalWarrantyQty,
        previousWarrantyLines: currentWarrantyLines,
        variantIdToSku,
      };
    } catch (error) {}
  }

  function handleCartUpdate(event) {
    const eventSource = event.detail?.data?.source;

    if (eventSource !== 'cart-warranty-sync') {
      if (syncTimeout) {
        clearTimeout(syncTimeout);
      }
      syncTimeout = setTimeout(() => {
        void syncWarrantyQuantity();
      }, 800);
    }

    setTimeout(() => {
      if (checkoutDisabledForCcoSync) {
        const btn = getCheckoutButton();
        if (btn) {
          btn.disabled = true;
          btn.setAttribute('aria-busy', 'true');
        }
      }
    }, CHECKOUT_REAPPLY_DISABLE_MS);
  }

  async function init() {
    document.addEventListener(ThemeEvents.cartUpdate, handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, handleCartUpdate);

    if (typeof window !== 'undefined' && window.location.pathname === '/cart') {
      try {
        const cart = await getCurrentCart();
        if (cart?.items?.length) {
          const ccoItems = createCcoLineItemTracker(cart.items);
          const warrantyLines = getWarrantyLineItems(cart.items);
          const warrantyQty = warrantyLines.reduce((sum, w) => sum + w.quantity, 0);
          const customColorData = createCustomColorVariantQuantityMap(cart.items);
          const variantIdToSku = createVariantIdToSkuMap(cart.items);
          previousCartState = {
            variantMap: createVariantQuantityMap(cart.items),
            warrantyVariantMap: createWarrantyVariantQuantityMap(cart.items),
            customColorVariantMap: customColorData.variantMap,
            customColorSkuMap: customColorData.skuMap,
            customColorCodeMap: customColorData.colorCodeMap,
            ccoLineItems: ccoItems,
            previousWarrantyQty: warrantyQty,
            previousWarrantyLines: warrantyLines,
            variantIdToSku,
          };
          await stripOrphanedWarrantyAttributesOnCart(cart.items);
        }
      } catch (e) {}
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => void init());
  } else {
    void init();
  }
})();
